import React, { useEffect, useState, useRef, Suspense, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, HeadphonesIcon, StarIcon, BookIcon, GlobeIcon, MailIcon, AwardIcon, UsersIcon, PlayIcon, PauseIcon, ClockIcon, CheckCircleIcon, TwitterIcon, InstagramIcon, XIcon, ChevronRightIcon, ThumbsUpIcon, MicIcon, MessageSquareIcon, InfoIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import AudiobookCard from '../components/Books/AudiobookCard';
import BackButton from '../components/UI/BackButton';
// --- Mock Data ---
const narratorData = {
  id: '1',
  name: 'Sarah Johnson',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Award-winning narrator with over 100 audiobooks in her portfolio. Known for bringing complex characters to life with emotional depth and nuanced performances. Sarah specializes in mystery, thriller, and romance genres, having voiced bestsellers for major publishers. When not in the booth, she teaches narration workshops and advocates for accessibility in audiobooks.',
  location: 'Los Angeles, CA',
  studio: 'Resonance Audio Studios',
  genres: ['Mystery', 'Thriller', 'Romance', 'Literary Fiction'],
  rating: 4.9,
  booksNarrated: 127,
  hoursNarrated: 1247,
  followers: '8.2k',
  availability: 'Available for Projects',
  collaboratedAuthors: 45,
  awards: ['AudioFile Earphones Award (2023)', 'Audie Award Finalist (2022)', 'Narrator of the Year (2021)', 'Voice Arts Award Nominee (2020)'],
  voiceCharacteristics: ['Warm and Rich Tone', 'Versatile Character Voices', 'Emotional Depth', 'Clear Pronunciation', 'Authentic Accents'],
  social: {
    website: 'https://sarahjohnson.com',
    twitter: '@sarahjnarrates',
    instagram: '@sarahjohnsonnarrator',
    email: 'contact@sarahjohnson.com'
  },
  featuredAudiobooks: [{
    title: 'The Silent Echo',
    author: 'Elena Richards',
    coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.8,
    audioLength: '10h 23m',
    releaseDate: 'June 2023',
    narrators: ['Sarah Johnson'],
    genre: 'Mystery',
    audioSampleUrl: '#',
    spiceLevel: 2,
    tropes: ['Small Town', 'Cold Case']
  }, {
    title: 'Whispers in the Dark',
    author: 'Maya Rodriguez',
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.7,
    audioLength: '8h 45m',
    releaseDate: 'March 2023',
    narrators: ['Sarah Johnson'],
    genre: 'Thriller',
    audioSampleUrl: '#',
    spiceLevel: 1,
    tropes: ['Psychological', 'Suspense']
  }, {
    title: 'Crimson Tide',
    author: 'James Patterson',
    coverImage: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.9,
    audioLength: '12h 10m',
    releaseDate: 'January 2023',
    narrators: ['Sarah Johnson', 'Mark Davis'],
    genre: 'Romance',
    audioSampleUrl: '#',
    spiceLevel: 4,
    tropes: ['Enemies to Lovers', 'Forbidden']
  }, {
    title: 'Midnight Garden',
    author: 'Sophie Blake',
    coverImage: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.6,
    audioLength: '9h 30m',
    releaseDate: 'November 2022',
    narrators: ['Sarah Johnson'],
    genre: 'Literary Fiction',
    audioSampleUrl: '#',
    spiceLevel: 0,
    tropes: ['Family Saga', 'Historical']
  }, {
    title: 'Starlight Dreams',
    author: 'Lucas Gray',
    coverImage: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=600&fit=crop',
    rating: 4.5,
    audioLength: '11h 15m',
    releaseDate: 'October 2022',
    narrators: ['Sarah Johnson'],
    genre: 'Fantasy',
    audioSampleUrl: '#',
    spiceLevel: 2,
    tropes: ['Magic System', 'Quest']
  }, {
    title: 'Broken Vows',
    author: 'Emma Cole',
    coverImage: 'https://images.unsplash.com/photo-1516414447565-b14be0adf13e?w=400&h=600&fit=crop',
    rating: 4.8,
    audioLength: '10h 05m',
    releaseDate: 'September 2022',
    narrators: ['Sarah Johnson', 'David Chen'],
    genre: 'Romance',
    audioSampleUrl: '#',
    spiceLevel: 3,
    tropes: ['Second Chance', 'Angst']
  }],
  sampleClips: [{
    id: '1',
    title: 'Emotional Monologue',
    bookTitle: 'The Silent Echo',
    author: 'Elena Richards',
    coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '2:30',
    genre: 'Drama',
    url: '#'
  }, {
    id: '2',
    title: 'Action Sequence',
    bookTitle: 'Whispers in the Dark',
    author: 'Maya Rodriguez',
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '1:45',
    genre: 'Thriller',
    url: '#'
  }, {
    id: '3',
    title: 'Romantic Dialogue',
    bookTitle: 'Crimson Tide',
    author: 'James Patterson',
    coverImage: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '3:10',
    genre: 'Romance',
    url: '#'
  }, {
    id: '4',
    title: 'Character Voices Showcase',
    bookTitle: 'Midnight Garden',
    author: 'Sophie Blake',
    coverImage: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '4:15',
    genre: 'Fiction',
    url: '#'
  }, {
    id: '5',
    title: 'Fantasy Narration',
    bookTitle: 'Starlight Dreams',
    author: 'Lucas Gray',
    coverImage: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=600&fit=crop',
    duration: '2:50',
    genre: 'Fantasy',
    url: '#'
  }],
  coNarrations: [{
    partnerId: '2',
    partnerName: 'Mark Davis',
    partnerAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
    sharedCount: 5,
    books: [{
      title: 'Crimson Tide',
      author: 'James Patterson',
      coverImage: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      rating: 4.9,
      audioLength: '12h 10m',
      narrators: ['Sarah Johnson', 'Mark Davis'],
      genre: 'Romance',
      audioSampleUrl: '#',
      spiceLevel: 4,
      tropes: ['Enemies to Lovers']
    }, {
      title: 'Velvet Night',
      author: 'James Patterson',
      coverImage: 'https://images.unsplash.com/photo-1495446815901-a7297e633e8d?w=400&h=600&fit=crop',
      rating: 4.7,
      audioLength: '11h 30m',
      narrators: ['Sarah Johnson', 'Mark Davis'],
      genre: 'Romance',
      audioSampleUrl: '#',
      spiceLevel: 3,
      tropes: ['Forbidden Love']
    }]
  }, {
    partnerId: '3',
    partnerName: 'David Chen',
    partnerAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
    sharedCount: 3,
    books: [{
      title: 'Broken Vows',
      author: 'Emma Cole',
      coverImage: 'https://images.unsplash.com/photo-1516414447565-b14be0adf13e?w=400&h=600&fit=crop',
      rating: 4.8,
      audioLength: '10h 05m',
      narrators: ['Sarah Johnson', 'David Chen'],
      genre: 'Romance',
      audioSampleUrl: '#',
      spiceLevel: 3,
      tropes: ['Second Chance']
    }]
  }],
  reviews: [{
    id: 'r1',
    reviewerName: 'Jessica M.',
    reviewerAvatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
    rating: 5,
    date: '2 weeks ago',
    text: "Sarah's narration is absolutely captivating. She distinguishes between characters so effortlessly that I sometimes forget it's just one person speaking. The emotional scenes in 'The Silent Echo' had me in tears.",
    helpfulCount: 24
  }, {
    id: 'r2',
    reviewerName: 'Michael T.',
    reviewerAvatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=100',
    rating: 5,
    date: '1 month ago',
    text: 'Hands down my favorite narrator for thrillers. Her pacing is perfect—she knows exactly when to speed up for tension and slow down for impact.',
    helpfulCount: 18
  }, {
    id: 'r3',
    reviewerName: 'BookLover99',
    reviewerAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
    rating: 4,
    date: '2 months ago',
    text: 'Great voice acting, though I wish the male voices were a bit deeper. Still, a fantastic performance overall.',
    helpfulCount: 6
  }, {
    id: 'r4',
    reviewerName: 'Alex R.',
    reviewerAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
    rating: 5,
    date: '3 months ago',
    text: 'I will listen to anything Sarah narrates. She elevates every book she touches.',
    helpfulCount: 42
  }],
  similarNarrators: [{
    id: 'n2',
    name: 'Julia Whelan',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200',
    books: 412,
    rating: 4.9
  }, {
    id: 'n3',
    name: 'Bahni Turpin',
    avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200',
    books: 350,
    rating: 4.8
  }, {
    id: 'n4',
    name: 'Davina Porter',
    avatar: 'https://images.unsplash.com/photo-1554151228-14d9def656ec?w=200',
    books: 280,
    rating: 4.9
  }, {
    id: 'n5',
    name: 'Imogen Church',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200',
    books: 190,
    rating: 4.7
  }]
};
// --- Components ---
const Waveform = ({
  isPlaying
}: {
  isPlaying: boolean;
}) => {
  return <div className="flex items-end gap-0.5 h-6" data-id="element-11709" data-mp="319h3v83uyf04">
      {[...Array(8)].map((_, i) => <motion.div key={i} className="w-1 bg-purple-500 rounded-t-sm" animate={{
      height: isPlaying ? [8, 16, 6, 20, 10, 24, 12, 8][i % 8] : 4
    }} transition={{
      duration: 0.6,
      repeat: Infinity,
      repeatType: 'reverse',
      delay: i * 0.1,
      ease: 'easeInOut'
    }} style={{
      height: 4
    }} data-id="element-11710" data-mp="1kw0eutw8wdhe" data-mpi={i} />)}
    </div>;
};
const StickyPlayer = ({
  isVisible,
  title,
  isPlaying,
  onTogglePlay,
  onClose
}: {
  isVisible: boolean;
  title: string;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onClose: () => void;
}) => {
  const {
    darkMode
  } = useDarkMode();
  return <AnimatePresence data-id="element-11711" data-mp="tp9z59pzqs5x">
      {isVisible && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} className={`fixed bottom-0 left-0 right-0 z-50 border-t shadow-lg backdrop-blur-md ${darkMode ? 'bg-gray-900/90 border-gray-700' : 'bg-white/90 border-gray-200'}`} data-id="element-11712" data-mp="tmdz9ful1teu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4" data-id="element-11713" data-mp="2b0icwh36xd90">
            <button onClick={onTogglePlay} className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center hover:bg-purple-700 transition-colors flex-shrink-0" data-id="element-11714" data-mp="2fyjsut5bkwg9">
              {isPlaying ? <PauseIcon className="w-5 h-5 fill-current" data-id="element-11715" data-mp="2othnq5s12ja8" /> : <PlayIcon className="w-5 h-5 fill-current ml-0.5" data-id="element-11716" data-mp="1s2yzz2dm3ahb" />}
            </button>

            <div className="flex-1 min-w-0" data-id="element-11717" data-mp="1x0hagx7a1xrf">
              <div className="flex items-center gap-3 mb-1" data-id="element-11718" data-mp="211b0bw4a5mmy">
                <h4 className={`font-medium text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-11719" data-mp="213x89wv3245p">
                  {title}
                </h4>
                <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`} data-id="element-11720" data-mp="3cgpxazwhoei2">
                  Now Playing
                </span>
              </div>
              <div className="h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-11721" data-mp="3mkms43p0ur3m">
                <motion.div className="h-full bg-purple-500" initial={{
              width: '0%'
            }} animate={{
              width: isPlaying ? '100%' : '0%'
            }} transition={{
              duration: 30,
              ease: 'linear'
            }} data-id="element-11722" data-mp="3htek9c3cbdsk" />
              </div>
            </div>

            <div className="hidden sm:block" data-id="element-11723" data-mp="2yxemfknolg12">
              <Waveform isPlaying={isPlaying} data-id="element-11724" data-mp="2uuia2boqig5k" />
            </div>

            <button onClick={onClose} className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11725" data-mp="2uw329unktn09">
              <XIcon className="w-5 h-5" data-id="element-11726" data-mp="105tnt4s21s57" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
const NarratorProfile = () => {
  const {
    id
  } = useParams();
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const [activeTab, setActiveTab] = useState<'audiobooks' | 'co-narrations' | 'samples' | 'about' | 'reviews'>('audiobooks');
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const audioRefs = useRef<{
    [key: string]: HTMLAudioElement;
  }>({});
  // Mock data usage
  const narrator = narratorData;
  const handlePlayPause = (trackId: string) => {
    if (currentlyPlaying === trackId) {
      setCurrentlyPlaying(null);
    } else {
      setCurrentlyPlaying(trackId);
    }
  };
  const tabs = [{
    key: 'audiobooks',
    label: 'Audiobooks',
    icon: BookIcon,
    count: narrator.booksNarrated
  }, {
    key: 'co-narrations',
    label: 'Co-Narrations',
    icon: UsersIcon,
    count: narrator.coNarrations.length
  }, {
    key: 'samples',
    label: 'Voice Samples',
    icon: MicIcon,
    count: narrator.sampleClips.length
  }, {
    key: 'reviews',
    label: 'Reviews',
    icon: MessageSquareIcon,
    count: narrator.reviews.length
  }, {
    key: 'about',
    label: 'About',
    icon: InfoIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-11727" data-mp="2qagcsylkn43">
      {/* Immersive Hero Section */}
      <div className="relative overflow-hidden" data-id="element-11728" data-mp="3tyekjuf0hpyo">
        {/* Blurred Background */}
        <div className="absolute inset-0 z-0" data-id="element-11729" data-mp="cgvg4jq0bxol">
          <div className={`absolute inset-0 ${dk.page} opacity-90 z-10`} data-id="element-11730" data-mp="7j3kbem8rrf4" />

          <div className={`absolute inset-0 bg-gradient-to-b ${darkMode ? 'from-purple-900/40 to-gray-900' : 'from-purple-100/50 to-gray-50'} z-10`} data-id="element-11731" data-mp="7l6mex0o5b07" />

          <img src={narrator.featuredAudiobooks[0].coverImage} alt="" className="w-full h-full object-cover filter blur-3xl opacity-50 scale-110" data-id="element-11732" data-mp="gl37yprrhtr1" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-11733" data-mp="3fbcmh0wtaels">
          <div className="mb-6" data-id="element-11734" data-mp="3javaw6nud7td">
            <BackButton className={darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-indigo-600'} data-id="element-11735" data-mp="3jallak6pl2o7" />
          </div>
          <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-11736" data-mp="1v5as4lz9no4">
            {/* Avatar */}
            <div className="flex-shrink-0 relative" data-id="element-11737" data-mp="6urnmcxw91we">
              <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full p-1 bg-gradient-to-br from-purple-500 to-indigo-500 shadow-xl" data-id="element-11738" data-mp="6swz6oneyrnk">
                <img src={narrator.avatar} alt={narrator.name} className={`w-full h-full rounded-full object-cover border-4 ${darkMode ? 'border-gray-800' : 'border-white'}`} data-id="element-11739" data-mp="28mwc1eg6ph2d" />
              </div>
              <div className={`absolute -bottom-2 -right-2 px-3 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1 ${narrator.availability.includes('Available') ? 'bg-green-100 text-green-800 dark:bg-green-900/80 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/80 dark:text-amber-300'}`} data-id="element-11740" data-mp="256jmtgcv9mjd">
                <div className={`w-2 h-2 rounded-full ${narrator.availability.includes('Available') ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-11741" data-mp="2o21g7nvplzrv" />

                {narrator.availability}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0" data-id="element-11742" data-mp="2a4364uof8pi6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-11743" data-mp="2a3a60cjywy90">
                <div data-id="element-11744" data-mp="13osrlmgol2fx">
                  <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-id="element-11745" data-mp="13l4y2nb8kg17">
                    {narrator.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm mb-4" data-id="element-11746" data-mp="2ypkzjb9ebcgp">
                    <div className="flex items-center gap-1.5" data-id="element-11747" data-mp="32shorktfy6tm">
                      <MapPinIcon className={`w-4 h-4 ${dk.textSecondary}`} data-id="element-11748" data-mp="32pwlqshepkua" />

                      <span className={dk.textSecondary} data-id="element-11749" data-mp="2ouaxinizz52x">
                        {narrator.location}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-11750" data-mp="3hgvnpknqlb4w">
                      <UsersIcon className={`w-4 h-4 ${dk.textSecondary}`} data-id="element-11751" data-mp="3hjhydt5xc6rc" />

                      <span className={dk.textSecondary} data-id="element-11752" data-mp="3mfffi1p0hh56">
                        {narrator.followers} Followers
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-11753" data-mp="3cpdpv5h0fqo5">
                      <HeadphonesIcon className={`w-4 h-4 ${dk.textSecondary}`} data-id="element-11754" data-mp="4avn9j9u93bx" />

                      <span className={dk.textSecondary} data-id="element-11755" data-mp="92dn2404kxvb">
                        {narrator.collaboratedAuthors} Author Collaborations
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3" data-id="element-11756" data-mp="1osea1ytwwrma">
                  <button className="px-5 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm flex items-center gap-2" data-id="element-11757" data-mp="22kkmpaxuwggz">
                    <MailIcon className="w-4 h-4" data-id="element-11758" data-mp="1asve04edl6qc" />
                    Contact
                  </button>
                  <button className={`px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors flex items-center gap-2 ${dk.card} ${dk.border} hover:${dk.surfaceMuted}`} data-id="element-11759" data-mp="15u1x01zvqxlf">
                    Follow
                  </button>
                </div>
              </div>

              {/* Bio with Truncation */}
              <div className="mb-6 max-w-3xl" data-id="element-11760" data-mp="2v6vnompdkjeb">
                <p className={`leading-relaxed ${dk.textSecondary} ${!isBioExpanded ? 'line-clamp-2' : ''}`} data-id="element-11761" data-mp="2qa58e1le9rty">
                  {narrator.bio}
                </p>
                <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="text-purple-600 hover:text-purple-700 text-sm font-medium mt-1 inline-flex items-center" data-id="element-11762" data-mp="2b1eu2sfjm0ji">
                  {isBioExpanded ? 'Read Less' : 'Read More'}
                  <ChevronRightIcon className={`w-3 h-3 ml-0.5 transition-transform ${isBioExpanded ? 'rotate-90' : ''}`} data-id="element-11763" data-mp="2yyu89ymfpgn2" />
                </button>
              </div>

              {/* Stats Row */}
              <div className={`flex flex-wrap gap-6 sm:gap-10 pt-6 border-t ${dk.border}`} data-id="element-11764" data-mp="38nvpfs4pu42m">
                <div className="flex flex-col" data-id="element-11765" data-mp="33qlzmbdezqe9">
                  <div className="flex items-center gap-1 mb-1" data-id="element-11766" data-mp="3hp3m65rl2wul">
                    <span className="text-2xl font-bold" data-id="element-11767" data-mp="3hn8yijoxgqmt">
                      {narrator.rating}
                    </span>
                    <div className="flex text-amber-400" data-id="element-11768" data-mp="763dfo7awhe">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < Math.floor(narrator.rating) ? 'fill-current' : `text-gray-300 dark:text-gray-600`}`} data-id="element-11769" data-mp="56978wsg2wns" data-mpi={i} />)}
                    </div>
                  </div>
                  <span className={`text-xs uppercase tracking-wider font-medium ${dk.textMuted}`} data-id="element-11770" data-mp="3dlkhdm6egdj7">
                    Rating
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-11771" data-mp="34mflyoq4x0b9">
                  <span className="text-2xl font-bold mb-1" data-id="element-11772" data-mp="38mpqry26xkoj">
                    {narrator.booksNarrated}
                  </span>
                  <span className={`text-xs uppercase tracking-wider font-medium ${dk.textMuted}`} data-id="element-11773" data-mp="2umopihi0kb8j">
                    Audiobooks
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-11774" data-mp="156mh62n9dd6c">
                  <span className="text-2xl font-bold mb-1" data-id="element-11775" data-mp="10f4ftdhbpaku">
                    {narrator.hoursNarrated}+
                  </span>
                  <span className={`text-xs uppercase tracking-wider font-medium ${dk.textMuted}`} data-id="element-11776" data-mp="wcpy4wpe4gv5">
                    Hours Narrated
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-11777" data-mp="1o3dyi6j8pi7r">
                  <div className="flex items-center gap-2 mb-1" data-id="element-11778" data-mp="1k33qkobw0hq9">
                    <span className="text-2xl font-bold" data-id="element-11779" data-mp="1k3vb943y4v1r">
                      {narrator.awards.length}
                    </span>
                    <AwardIcon className="w-5 h-5 text-amber-500" data-id="element-11780" data-mp="1a4wbyc52mbrf" />
                  </div>
                  <span className={`text-xs uppercase tracking-wider font-medium ${dk.textMuted}`} data-id="element-11781" data-mp="1a7a3ib0jl5u8">
                    Awards
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className={`sticky top-0 z-30 border-b ${dk.surface} ${dk.border} backdrop-blur-sm`} data-id="element-11782" data-mp="3pq1ejtx2y5vc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11783" data-mp="3up4b0pyzv3rp">
          <div className="flex overflow-x-auto scrollbar-hide" data-id="element-11784" data-mp="3unjewvx17crq">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return <button key={tab.key} onClick={() => setActiveTab(tab.key as any)} className={`relative flex items-center gap-2 py-4 px-6 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'text-purple-600' : `${dk.textMuted} hover:${dk.textPrimary}`}`} data-id="element-11785" data-mp="cb4sltiuwm5s">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-purple-600' : ''}`} data-id="element-11786" data-mp="2j35v5n3k6951" />

                  {tab.label}
                  {tab.count !== undefined && <span className={`ml-1.5 text-xs py-0.5 px-2 rounded-full ${isActive ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : dk.badgeNeutral}`} data-id="element-11787" data-mp="ib3qplctojv4">
                      {tab.count}
                    </span>}
                  {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" data-id="element-11788" data-mp="3utsiycyd7lu2" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24" data-id="element-11789" data-mp="3nip5vvlispb">
        <AnimatePresence mode="wait" data-id="element-11790" data-mp="3c3khdzabd3fz">
          {/* Audiobooks Tab */}
          {activeTab === 'audiobooks' && <motion.div key="audiobooks" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-11791" data-mp="3750xs5j703qs">
              {narrator.featuredAudiobooks.map((book, idx) => <div key={idx} className="w-full" data-id="element-11792" data-mp="268le2ks19jk7" data-mpi={idx}>
                  <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} audioSampleUrl={book.audioSampleUrl} isPlaying={currentlyPlaying === book.title} onPlayPause={() => handlePlayPause(book.title)} spiceLevel={book.spiceLevel} tropes={book.tropes} data-id="element-11793" data-mp="2aazvr1jyud9w" />
                </div>)}
            </motion.div>}

          {/* Co-Narrations Tab */}
          {activeTab === 'co-narrations' && <motion.div key="co-narrations" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-12" data-id="element-11794" data-mp="3o82k9hnl6vcl">
              {narrator.coNarrations.map(partner => <div key={partner.partnerId} className={`rounded-2xl p-6 ${dk.card} border`} data-id="element-11795" data-mp="18hgagp2v5frv">
                  <div className="flex items-center gap-4 mb-6" data-id="element-11796" data-mp="13ilhwvf3gcuk">
                    <img src={partner.partnerAvatar} alt={partner.partnerName} className="w-16 h-16 rounded-full object-cover border-2 border-purple-100 dark:border-purple-900" data-id="element-11797" data-mp="1b0bbt09rq4iw" />

                    <div data-id="element-11798" data-mp="1pq92pnz318bs">
                      <h3 className="text-xl font-bold" data-id="element-11799" data-mp="1pqimgttjaj91">
                        {partner.partnerName}
                      </h3>
                      <div className="flex items-center gap-2 mt-1" data-id="element-11800" data-mp="s41pxqsmk9oc">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`} data-id="element-11801" data-mp="x0s6sgc979at">
                          Narrated {partner.sharedCount} books together
                        </span>
                        <Link to={`/narrators/${partner.partnerId}`} className="text-sm text-purple-600 hover:underline flex items-center" data-id="element-11802" data-mp="2dfa5aul8hp95">
                          View Profile{' '}
                          <ChevronRightIcon className="w-3 h-3 ml-0.5" data-id="element-11803" data-mp="2ykdwo9ja53fm" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-11804" data-mp="38i1s71opmeyu">
                    {partner.books.map((book, idx) => <div key={idx} data-id="element-11805" data-mp="3m859de8qfihf" data-mpi={idx}>
                        <AudiobookCard title={book.title} author={book.author} coverImage={book.coverImage} narrators={book.narrators} audioLength={book.audioLength} genre={book.genre} audioSampleUrl={book.audioSampleUrl} isPlaying={currentlyPlaying === book.title} onPlayPause={() => handlePlayPause(book.title)} spiceLevel={book.spiceLevel} tropes={book.tropes} data-id="element-11806" data-mp="3mb9oh6nqjymv" />
                      </div>)}
                  </div>
                </div>)}
            </motion.div>}

          {/* Voice Samples Tab */}
          {activeTab === 'samples' && <motion.div key="samples" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="max-w-3xl mx-auto space-y-4" data-id="element-11807" data-mp="7jep7ff1weu7">
              {narrator.sampleClips.map((clip, idx) => {
            const isPlaying = currentlyPlaying === clip.id;
            return <div key={clip.id} className={`group/card flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md ${isPlaying ? darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200' : `${dk.card} border hover:${dk.borderSubtle}`}`} data-id="element-11808" data-mp="2kt2ns4253hu2" data-mpi={idx}>
                    {/* Cover image with hover play overlay */}
                    <button onClick={() => handlePlayPause(clip.id)} className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 group/cover" data-id="element-11809" data-mp="34hbfkczrtlt1">
                      <img src={clip.coverImage} alt={clip.bookTitle} className="w-full h-full object-cover" data-id="element-11810" data-mp="3dhrjfnkfeuyf" />

                      {/* Play/Pause overlay */}
                      <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isPlaying ? 'bg-black/50 opacity-100' : 'bg-black/40 opacity-0 group-hover/cover:opacity-100'}`} data-id="element-11811" data-mp="1o1s1pi2qrx76">
                        {isPlaying ? <PauseIcon className="w-5 h-5 text-white fill-current" data-id="element-11812" data-mp="16sfzbon46kjs" /> : <PlayIcon className="w-5 h-5 text-white fill-current ml-0.5" data-id="element-11813" data-mp="11unz87cbfs4i" />}
                      </div>
                      {/* Playing indicator ring */}
                      {isPlaying && <div className="absolute inset-0 rounded-lg ring-2 ring-purple-500 ring-offset-1 ring-offset-transparent" data-id="element-11814" data-mp="1ujbn8aog8vlr" />}
                    </button>

                    <div className="flex-1 min-w-0" data-id="element-11815" data-mp="1kjksq8zw2glo">
                      <div className="flex items-center gap-2 mb-1" data-id="element-11816" data-mp="e4223b3uns9a">
                        <h3 className={`font-semibold text-sm truncate ${isPlaying ? 'text-purple-600 dark:text-purple-400' : ''}`} data-id="element-11817" data-mp="e27dnmtddi0g">
                          {clip.bookTitle}
                        </h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${dk.badgeNeutral}`} data-id="element-11818" data-mp="31bq0aelhys01">
                          {clip.genre}
                        </span>
                      </div>
                      <p className={`text-xs ${dk.textMuted}`} data-id="element-11819" data-mp="2dn6tk3i3ina2">
                        by <span className="font-medium" data-id="element-11820" data-mp="2dlvnfx16h3fs">{clip.author}</span>
                      </p>
                    </div>

                    <div className="flex items-center gap-4" data-id="element-11821" data-mp="2ifq2gn5u8u8j">
                      {isPlaying && <div className="hidden sm:block" data-id="element-11822" data-mp="29ll7psnl2ynq">
                          <Waveform isPlaying={true} data-id="element-11823" data-mp="29m3bdacksf5o" />
                        </div>}
                      <div className={`flex items-center gap-1.5 text-xs font-medium ${dk.textMuted}`} data-id="element-11824" data-mp="1up2uy2zu46mi">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-11825" data-mp="1g12jhe1zhfdx" />
                        {clip.duration}
                      </div>
                    </div>
                  </div>;
          })}
            </motion.div>}

          {/* About Tab */}
          {activeTab === 'about' && <motion.div key="about" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-11826" data-mp="1l0fdcir9vmq0">
              <div className="lg:col-span-2 space-y-8" data-id="element-11827" data-mp="2io7c3h5zavzp">
                <div className={`p-8 rounded-2xl ${dk.card} shadow-sm`} data-id="element-11828" data-mp="2iop76agwn461">
                  <h2 className="text-xl font-bold mb-4" data-id="element-11829" data-mp="2dqnohqne56v3">Biography</h2>
                  <p className={`leading-relaxed whitespace-pre-line ${dk.textSecondary}`} data-id="element-11830" data-mp="b38p19niz7x6">
                    {narrator.bio}
                  </p>
                </div>

                <div className={`p-8 rounded-2xl ${dk.card} shadow-sm`} data-id="element-11831" data-mp="ks8p6wdrk9cy">
                  <h2 className="text-xl font-bold mb-6" data-id="element-11832" data-mp="prbumhxnohlg">
                    Voice Characteristics
                  </h2>
                  <div className="flex flex-wrap gap-3" data-id="element-11833" data-mp="3oa0o6uqbhtkn">
                    {narrator.voiceCharacteristics.map((trait, idx) => <span key={idx} className={`px-4 py-2 rounded-lg text-sm font-medium ${dk.cardElevated}`} data-id="element-11834" data-mp="3t8tykeab54gl" data-mpi={idx}>
                        {trait}
                      </span>)}
                  </div>
                </div>

                <div className={`p-8 rounded-2xl ${dk.card} shadow-sm`} data-id="element-11835" data-mp="1dja898kkup1r">
                  <h2 className="text-xl font-bold mb-6" data-id="element-11836" data-mp="1iexupg7od43k">
                    Awards & Recognition
                  </h2>
                  <div className="space-y-4" data-id="element-11837" data-mp="32xiahf8pzzjx">
                    {narrator.awards.map((award, idx) => <div key={idx} className="flex items-start gap-4" data-id="element-11838" data-mp="2y1b81wfj966q" data-mpi={idx}>
                        <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" data-id="element-11839" data-mp="2t6xabndeemyw">
                          <AwardIcon className="w-5 h-5" data-id="element-11840" data-mp="2o7kon5y9jlxw" />
                        </div>
                        <div data-id="element-11841" data-mp="2j87qvq5usugj">
                          <p className="font-medium" data-id="element-11842" data-mp="2f8yx4latcwkz">{award}</p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-11843" data-mp="25b2pw4kzyls5">
                <div className={`p-6 rounded-2xl ${dk.card} shadow-sm`} data-id="element-11844" data-mp="25c43afphjkd1">
                  <h3 className="font-bold mb-4" data-id="element-11845" data-mp="8h7xth063d9d">Studio Info</h3>
                  <div className="space-y-3 text-sm" data-id="element-11846" data-mp="3j6d6rn3f5vs">
                    <div className="flex items-center gap-3" data-id="element-11847" data-mp="3jpmxl2lanyq">
                      <MicIcon className="w-4 h-4 text-purple-500" data-id="element-11848" data-mp="1xyv13ml5fw9y" />
                      <span data-id="element-11849" data-mp="1xxjxpnvm8qjd">{narrator.studio}</span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-11850" data-mp="22rfuin0587cy">
                      <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-11851" data-mp="1o207ni3qgm2j" />
                      <span data-id="element-11852" data-mp="1t01y4o3hnno1">Home Studio Certified</span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-11853" data-mp="1sxyx790ssa4j">
                      <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-11854" data-mp="2gqfhzrbx6g7j" />
                      <span data-id="element-11855" data-mp="2lp8tje7nbx52">Source-Connect Standard</span>
                    </div>
                  </div>
                </div>

                <div className={`p-6 rounded-2xl ${dk.card} shadow-sm`} data-id="element-11856" data-mp="26r6p71ssj7mr">
                  <h3 className="font-bold mb-4" data-id="element-11857" data-mp="2bprtbcb5ybgh">Connect</h3>
                  <div className="space-y-4" data-id="element-11858" data-mp="vcjh6pzobf8y">
                    <a href={narrator.social.website} className={`flex items-center gap-3 text-sm hover:text-purple-600 transition-colors ${dk.textSecondary}`} data-id="element-11859" data-mp="vbrw493pczwx">
                      <GlobeIcon className="w-4 h-4" data-id="element-11860" data-mp="3eli4q8no3bjr" />
                      Website
                    </a>
                    <a href={`mailto:${narrator.social.email}`} className={`flex items-center gap-3 text-sm hover:text-purple-600 transition-colors ${dk.textSecondary}`} data-id="element-11861" data-mp="39mf1mtr8zfef">
                      <MailIcon className="w-4 h-4" data-id="element-11862" data-mp="2vse8cfj2eklg" />
                      {narrator.social.email}
                    </a>
                    <a href="#" className={`flex items-center gap-3 text-sm hover:text-purple-600 transition-colors ${dk.textSecondary}`} data-id="element-11863" data-mp="2qv49k99scwky">
                      <TwitterIcon className="w-4 h-4" data-id="element-11864" data-mp="2ztpqjzzm03jm" />
                      {narrator.social.twitter}
                    </a>
                    <a href="#" className={`flex items-center gap-3 text-sm hover:text-purple-600 transition-colors ${dk.textSecondary}`} data-id="element-11865" data-mp="2c2u0b7asxgeh">
                      <InstagramIcon className="w-4 h-4" data-id="element-11866" data-mp="2x0gtkx2u04sb" />
                      {narrator.social.instagram}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && <motion.div key="reviews" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="max-w-4xl mx-auto" data-id="element-11867" data-mp="3aviyphzutq42">
              <div className="flex items-center justify-between mb-8" data-id="element-11868" data-mp="2jn23qzxda7y">
                <div data-id="element-11869" data-mp="7gdi6ebqc5td">
                  <h2 className="text-2xl font-bold" data-id="element-11870" data-mp="7hyd451yhqrr">Listener Reviews</h2>
                  <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-11871" data-mp="hlde0ivau18k">
                    Based on {narrator.reviews.length} reviews for narration
                    quality
                  </p>
                </div>
                <div className="flex items-center gap-4" data-id="element-11872" data-mp="7nyxkj9dlcg9">
                  <div className="text-right" data-id="element-11873" data-mp="3lc46dx23jgy">
                    <div className="text-3xl font-bold text-purple-600" data-id="element-11874" data-mp="3mnfqzwqtvim">
                      {narrator.rating}
                    </div>
                    <div className="flex text-amber-400 text-xs" data-id="element-11875" data-mp="10dfm35tvk977">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3 h-3 ${i < Math.floor(narrator.rating) ? 'fill-current' : 'text-gray-300'}`} data-id="element-11876" data-mp="qljl9n0em4za" data-mpi={i} />)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-11877" data-mp="1eab8s131tz5q">
                {narrator.reviews.map(review => <div key={review.id} className={`p-6 rounded-xl ${dk.card} shadow-sm`} data-id="element-11878" data-mp="39ocgojp2ac0z">
                    <div className="flex justify-between items-start mb-4" data-id="element-11879" data-mp="34snes9pkkj1i">
                      <div className="flex items-center gap-3" data-id="element-11880" data-mp="2zxrmd8gf7tnv">
                        <img src={review.reviewerAvatar} alt={review.reviewerName} className="w-10 h-10 rounded-full" data-id="element-11881" data-mp="2vysjdte926z2" />

                        <div data-id="element-11882" data-mp="2ly8emgriukc3">
                          <h4 className="font-bold text-sm" data-id="element-11883" data-mp="2m2619cdb5u0q">
                            {review.reviewerName}
                          </h4>
                          <span className={`text-xs ${dk.textMuted}`} data-id="element-11884" data-mp="2czwtgu9fcer1">
                            {review.date}
                          </span>
                        </div>
                      </div>
                      <div className="flex text-amber-400" data-id="element-11885" data-mp="b7i8fmbl2u6e">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-current' : 'text-gray-300'}`} data-id="element-11886" data-mp="1t4lmai3ud7ux" data-mpi={i} />)}
                      </div>
                    </div>
                    <p className={`text-sm leading-relaxed mb-4 ${dk.textSecondary}`} data-id="element-11887" data-mp="26vqg33kj35xc">
                      "{review.text}"
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-11888" data-mp="1f4u6cbtrlhea">
                      <button className="flex items-center gap-1 hover:text-purple-600 transition-colors" data-id="element-11889" data-mp="1j6ph06xl574q">
                        <ThumbsUpIcon className="w-3.5 h-3.5" data-id="element-11890" data-mp="1o6k7i9dg7up9" />
                        Helpful ({review.helpfulCount})
                      </button>
                    </div>
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Similar Narrators Section */}
        <div className={`mt-20 pt-10 border-t ${dk.border}`} data-id="element-11891" data-mp="zheecbwfrp3k">
          <h2 className={`text-xl font-bold mb-6 ${dk.textHeading}`} data-id="element-11892" data-mp="vidxgyxiqwj7">
            Similar Narrators
          </h2>
          <div className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide" data-id="element-11893" data-mp="19g2kacb5cdqw">
            {narrator.similarNarrators.map(sim => <Link key={sim.id} to={`/narrators/${sim.id}`} className={`flex-shrink-0 w-64 p-4 rounded-xl border transition-all hover:shadow-md ${dk.card} hover:${dk.borderSubtle}`} data-id="element-11894" data-mp="14g7t07nglm5b">
                <div className="flex items-center gap-4 mb-3" data-id="element-11895" data-mp="qisywpm9gq70">
                  <img src={sim.avatar} alt={sim.name} className="w-14 h-14 rounded-full object-cover" data-id="element-11896" data-mp="qkcjgsey9nel" />

                  <div data-id="element-11897" data-mp="3u5jl2u3zqo6t">
                    <h3 className="font-bold text-sm" data-id="element-11898" data-mp="3p8jgyoqpbvic">{sim.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-amber-500" data-id="element-11899" data-mp="3p89pewq0dgaj">
                      <StarIcon className="w-3 h-3 fill-current" data-id="element-11900" data-mp="24oxu0wbrgxpj" />
                      <span data-id="element-11901" data-mp="29hh4vlj2deqn">{sim.rating}</span>
                    </div>
                  </div>
                </div>
                <div className={`text-xs ${dk.textMuted}`} data-id="element-11902" data-mp="1vr3xpldzy42d">
                  {sim.books} Audiobooks
                </div>
              </Link>)}
          </div>
        </div>
      </div>

      {/* Sticky Mini Player */}
      <StickyPlayer isVisible={!!currentlyPlaying} title={narrator.sampleClips.find(c => c.id === currentlyPlaying)?.title || narrator.featuredAudiobooks.find(b => b.title === currentlyPlaying)?.title || 'Audio Clip'} isPlaying={!!currentlyPlaying} onTogglePlay={() => setCurrentlyPlaying(null)} onClose={() => setCurrentlyPlaying(null)} data-id="element-11903" data-mp="2nevzd4o6ccph" />
    </div>;
};
export default NarratorProfile;